import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Input from '../ui/Input';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import type { E164Number } from 'libphonenumber-js';
import 'react-datepicker/dist/react-datepicker.css';
import i18n from '../../i18n';
import { Circles } from "react-loader-spinner";
import { useDispatch, useSelector } from 'react-redux';
import { registerUser, sendOtpRegister } from '../../features/auth/registerSlice';
import { AppDispatch, RootState } from '../../store';
import toast from 'react-hot-toast';
import OtpStep from './OtpStep';
import { Link, useNavigate } from 'react-router-dom';
import { useSEO } from '../../custom hooks/useSEO';

const RegisterForm = () => {
    const { t } = useTranslation();
    const { t: tSeo } = useTranslation("seo");

    const seo = useSEO({
        title: tSeo("register.title"),
        description: tSeo("register.description")
    });

    const dispatch = useDispatch<AppDispatch>();
    const { loading } = useSelector((state: RootState) => state.register);
    const navigate = useNavigate();

    const [step, setStep] = useState<"form" | "otp">("form");

    // new fields
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');

    const [phone, setPhone] = useState<E164Number | undefined>();
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const isValidPassword = (password: string) => {
        const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
        return regex.test(password);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            toast.error(t("password-not-matches"))
        } else if (!isValidPassword(password)) {
            toast.error(t("password-invalid"));
        } else {
            if (phone) {
                const res = await dispatch(sendOtpRegister(phone)).unwrap();
                if (res?.success) {
                    setStep("otp");
                }
            }
        }
    };

    const handleVerified = () => {
        if (!phone) return;
        dispatch(
            registerUser({
                data: {
                    name: `${firstName} ${lastName}`,
                    email,
                    password,
                    phone: phone as string,
                }, navigate, dispatch
            }));
    };

    const handleResend = async () => {
        if (phone) {
            dispatch(sendOtpRegister(phone)).unwrap();

        }
    };

    if (step === "otp") {
        return <OtpStep phone={String(phone)} onVerified={handleVerified} onResend={handleResend} />
    }

    return (
        <div dir={i18n.language === "ar" ? "rtl" : "ltr"}  >
            {seo}
            <div className=' mb-5'>
                <h1 className=' text-4xl font-bold'>{t("signup")}</h1>
                <p className=' text-lg text-textSecond mt-4'>{t("signup-subheading")}</p>
            </div>
            <form className=' relative text-black ' onSubmit={handleSubmit}>
                <div className='grid grid-cols-1 gap-4'>
                    {/* First name */}
                    <Input
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        type='text'
                        name='firstName'
                        text={t("first-name")}
                        required
                    />
                    {/* Last name */}
                    <Input
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        type='text'
                        name='lastName'
                        text={t("last-name")}
                        required
                    />
                    {/* Email */}
                    <Input
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        type='email'
                        name='email'
                        text={t("email")}
                        required
                    />

                    <div className="flex flex-col gap-2">
                        <label htmlFor="phone" className=' font-medium text-lg '>
                            {t("number")}
                        </label>
                        <PhoneInput
                            id="phone"
                            international
                            defaultCountry="LY"
                            value={phone}
                            onChange={setPhone}
                            className="phone-input border w-full h-full border-grayColor rounded-lg p-3 bg-white focus:outline-none"
                        />
                    </div>

                    <Input
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        type='password'
                        name='password'
                        text={t("password")}
                        required
                    />
                    <Input
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        type='password'
                        name='confirm-password'
                        text={t("confirm-password")}
                        required
                    />
                </div>

                <div className='w-full bg-secondry rounded-lg py-3 mt-5 text-white cursor-pointer flex items-center justify-center'>
                    {loading ? (
                        <Circles
                            height="20"
                            width="20"
                            color="#fff"
                            ariaLabel="circles-loading"
                            visible={true}
                        />
                    ) : (
                        <input type="submit" value={t("register")} className='w-full cursor-pointer' />
                    )}
                </div>
            </form>
            {/* Login Link */}
            <div className='pt-5 flex gap-1 justify-center'>
                <p className='text-grayColor'>{t("you-have-account")}</p>
                <Link className='text-secondry' to="/auth/login">{t("login")}</Link>
            </div>
        </div>
    )
}

export default RegisterForm;
